import "./Unanswered.scss";
import { AuthContext } from "../../../contexts/AuthContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Button from "../../../components/Button/Button";
import AlertModal from "../../../components/AlertModal/AlertModal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../../components/Modal/Modal";
import PageTitle from "../../../components/PageTitle/PageTitle";
import {
  DynamicSelect,
  DynamicTextarea,
} from "../../../components/Inputs/Inputs";
import { ReactComponent as User } from "../../../assets/user.svg";
import { ReactComponent as Check } from "../../../assets/check-circle.svg";
import { ReactComponent as Edit } from "../../../assets/edit.svg";
import { ReactComponent as Trash } from "../../../assets/x.svg";
import Pagination from "../../../components/Pagination/Pagination";

export default function Unanswered() {
  const { axiosAuth, currentUser } = useContext(AuthContext);
  // editar/eliminar pedidos é exclusivo do superadmin (o backend também o valida)
  const isSuperadmin = currentUser?.role === "superadmin";
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [hotelsById, setHotelsById] = useState(() => new Map());
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  // paginação
  const [page, setPage] = useState(1);
  const limit = 20;
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.ceil(total / limit), [total]);

  // estado para saber quais mensagens estão a ser marcadas
  const [updatingIds, setUpdatingIds] = useState(() => new Set());

  // edição / eliminação de pedidos (apenas superadmin)
  const [editing, setEditing] = useState(null); // { card, question }
  const [deleting, setDeleting] = useState(null); // card
  const [saving, setSaving] = useState(false);

  const openError = useCallback((title, message) => {
    setErrorModal({
      open: true,
      title,
      message,
    });
  }, []);

  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  function formatDateTime(iso) {
    if (!iso) return;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("pt-PT"); //dd/mm/aaaa hh:mm
  }

  const fetchGroups = useCallback(async () => {
    try {
      const { data } = await axiosAuth.get("/groups/");
      const list = Array.isArray(data) ? data : [];
      setGroups(list);
      if (list.length > 0 && !selectedGroupId) {
        setSelectedGroupId(String(list[0].id)); // auto seleciona o 1º
      }
    } catch (err) {
      openError(
        "Erro ao carregar grupos",
        err?.response?.data?.detail || err.message
      );
    }
  }, [axiosAuth, openError, selectedGroupId]);

  const fetchHotelsOfGroup = useCallback(
    async (groupId) => {
      if (!groupId) {
        setHotelsById(new Map());
        return;
      }
      try {
        const { data } = await axiosAuth.get(`/hotels/group/${groupId}`);
        const map = new Map();
        (data || []).forEach((h) => map.set(Number(h.id), h.name));
        setHotelsById(map);
      } catch (err) {
        console.warn(
          "Falha ao carregar hotéis",
          err?.response?.data?.detail || err.message
        );
        setHotelsById(new Map());
      }
    },
    [axiosAuth]
  );

  // carrega mensagens do grupo e produz cards de unanswered
  const fetchUnanswered = useCallback(
    async (groupId) => {
      if (!groupId) {
        setCards([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/chat-messages/group/${groupId}/unaswered`,
          {
            params: {
              skip: (page - 1) * limit,
              limit,
            },
          }
        );
        const results = Array.isArray(data.results) ? data.results : [];
        const totalCount = Number.isFinite(data?.total) ? data.total : 0;

        // ordenar por data desc (mais recentes primeiro)
        setCards(results);
        setTotal(totalCount);
      } catch (err) {
        openError(
          "Erro ao carregar mensagens sem resposta",
          err?.response?.data?.detail || err.message
        );
      } finally {
        setLoading(false);
      }
    },
    [axiosAuth, openError, page, limit]
  );

  const handleMarkAsChecked = useCallback(
    async (messageId) => {
      // marcar como atualizar (desativa o botão)
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.add(messageId);
        return next;
      });

      try {
        await axiosAuth.patch(`/chat-messages/${messageId}/checked`);

        // remover da UI
        setCards((prev) => prev.filter((c) => c.id !== messageId));

        // atualizar total para manter paginação coerente
        setTotal((prev) => Math.max(0, prev - 1));
      } catch (err) {
        openError(
          "Erro ao marcar mensagem como revista.",
          err?.response?.data?.detail || err.message
        );
      } finally {
        // limpar estado de loading desse card
        setUpdatingIds((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
      }
    },
    [axiosAuth, openError]
  );

  const handleSaveEdit = useCallback(async () => {
    if (!editing) return;
    const { card, question } = editing;
    const content = question.trim();
    if (!content) {
      openError("Pergunta inválida", "A pergunta não pode ficar vazia.");
      return;
    }
    setSaving(true);
    try {
      await axiosAuth.patch(`/chat-messages/${card.visitor_message_id}`, {
        content,
      });
      setCards((prev) =>
        prev.map((c) =>
          c.id === card.id ? { ...c, visitor_question: content } : c
        )
      );
      setEditing(null);
    } catch (err) {
      openError(
        "Erro ao editar o pedido.",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
    }
  }, [axiosAuth, editing, openError]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleting) return;
    const card = deleting;
    setDeleting(null);
    setUpdatingIds((prev) => new Set(prev).add(card.id));
    try {
      await axiosAuth.delete(`/chat-messages/${card.id}`);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      openError(
        "Erro ao eliminar o pedido.",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(card.id);
        return next;
      });
    }
  }, [axiosAuth, deleting, openError]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // sempre que muda o grupo, volta para a página 1
  useEffect(() => {
    setPage(1);
  }, [selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId) return;
    fetchHotelsOfGroup(selectedGroupId);
  }, [selectedGroupId, fetchHotelsOfGroup]);

  useEffect(() => {
    if (!selectedGroupId) return;
    fetchUnanswered(selectedGroupId); // dispara quando selectedGroupId OU page mudam
  }, [selectedGroupId, page, fetchUnanswered]);

  // opções de dropdown
  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: String(g.id), label: g.name })),
    [groups]
  );

  return (
    <div className="unanswered-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      {deleting && (
        <ConfirmationModal
          title="Eliminar pedido"
          variant="danger"
          message="Tem a certeza que pretende eliminar este pedido sem resposta? Esta ação não pode ser revertida."
          secText="Cancelar"
          varText="Eliminar"
          onCancel={() => setDeleting(null)}
          onConfirm={handleConfirmDelete}
        />
      )}

      <Modal
        isVisible={Boolean(editing)}
        title="Editar pedido"
        onClose={() => !saving && setEditing(null)}
        onSave={handleSaveEdit}
        disableActions={saving}
      >
        <DynamicTextarea
          label="Pergunta do visitante"
          name="question"
          rows={5}
          value={editing?.question || ""}
          onChange={(_, value) =>
            setEditing((prev) => (prev ? { ...prev, question: value } : prev))
          }
        />
      </Modal>

      <PageTitle title="Mensagens sem resposta" />

      <div className="filters">
        <DynamicSelect
          label="Cliente"
          placeholder="Escolher um grupo"
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          options={groupOptions}
          disabled={loading}
        />
        {/* <Button type="button" onClick={() => fetchUnanswered(selectedGroupId)}>
          Recarregar
        </Button> */}
      </div>

      <div className="container">
        {!loading && cards.length === 0 && selectedGroupId && (
          <div className="empty">Sem mensagens por responder neste grupo.</div>
        )}
        <div className="messages-list">
          {cards.map((c) => (
            <div key={c.id} className="unanswered-card">
              <button
                type="button"
                onClick={() => handleMarkAsChecked(c.id)}
                disabled={loading || updatingIds.has(c.id)}
                className="checked-btn"
              >
                <Check />
              </button>
              <div className="top">
                <div className="left">
                  <div className="icon">
                    <User />
                  </div>
                  <span className="status-dot green" />
                </div>
                <div className="right">
                  <div className="line">
                    <span className="label">Sessão: </span>
                    <span className="value mono">{c.session_id}</span>
                  </div>
                  <div className="line date">
                    {/* <span className="label">Data: </span> */}
                    <span className="value">{formatDateTime(c.timestamp)}</span>
                  </div>
                  {c.hotel_id && hotelsById.get(Number(c.hotel_id)) && (
                    <div className="line hotel">
                      {/* <span className="label">Hotel:</span> */}
                      <span className="value">
                        {hotelsById.get(Number(c.hotel_id))}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="bottom">
                {c.visitor_question ? (
                  <div className="message-block">
                    <p className="message">{c.visitor_question}</p>
                  </div>
                ) : null}
              </div>
              {isSuperadmin && (
                <div className="admin-actions">
                  <button
                    type="button"
                    className="action-btn edit"
                    title={
                      c.visitor_message_id
                        ? "Editar pedido"
                        : "Sem pergunta do visitante para editar"
                    }
                    onClick={() =>
                      setEditing({ card: c, question: c.visitor_question || "" })
                    }
                    disabled={
                      loading || updatingIds.has(c.id) || !c.visitor_message_id
                    }
                  >
                    <Edit />
                    <span>Editar</span>
                  </button>
                  <button
                    type="button"
                    className="action-btn delete"
                    title="Eliminar pedido"
                    onClick={() => setDeleting(c)}
                    disabled={loading || updatingIds.has(c.id)}
                  >
                    <Trash />
                    <span>Eliminar</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {total > limit && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
        />
      )}
    </div>
  );
}
